/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class VisDndLiveWidgetProvider
 * @hideconstructor
 *
 * @classdesc Handles drag and drop of metadata to create a visualization.
 */
define([
	'underscore',
	'jquery',
	'dashboard-analytics/widgets/livewidget/nls/StringResources'
], (
	_, $, StringResources
) => {

	return class VisDndLiveWidgetProvider {

		constructor(options = {}) {
			this.dashboardAPI = options.dashboardAPI;
			this.dashboardDnD = options.dashboardDnD;
			this.content = options.content;
			this.visualization = options.visualization;
			this.state = options.state;
			this.visRecommender = options.visRecommender;
			this.visDnDUtils = options.visDnDUtils;
			this.transaction = options.transaction;
		}

		destroy () {
			this.dashboardAPI = null;
			this.dashboardDnD = null;
			this.content = null;
			this.visualization = null;
			this.state = null;
			this.visRecommender = null;
			this.visDnDUtils = null;
			this.transaction = null;
		}

		isInvalidVisDefinition (target) {
			const $errorContainer = $(target.info.node).find('.vis-sdk-error-container-wrapper');
			if ($errorContainer.length > 0) {
				return true;
			}

			const definition = this.visualization.getDefinition();
			return definition && definition.getState().getError() !== undefined;

		}

		/**
		 * Adds the model filters among the mappings to the local filters.
		 * @param metadataColumns an array of metadataColumns to be added to the visualization
		 * @returns remaining mappings that are not a model filter
		 */
		addModelFilters(metadataColumns, transactionToken) {
			var modelFilters = [];

			var nonFilterMetadataColumns = _.filter(metadataColumns, function(metadataColumn) {
				if (metadataColumn.isFilter()) {
					modelFilters.push(metadataColumn.getId());
					return false;
				}
				return true;
			});

			if (modelFilters.length > 0) {
				this._setModelFilter(modelFilters, transactionToken);
			}

			return nonFilterMetadataColumns;
		}

		_setModelFilter(modelfilterId, transactionToken) {
			var localFilters = this.visualization.getLocalFilters();

			modelfilterId = $.isArray(modelfilterId) ? modelfilterId : [modelfilterId];

			_.each(modelfilterId, function(id) {
				const itemContext = {
					id: id,
					itemId: id,
					filterType: 'filter'
				};
				localFilters.addFilter(itemContext, {} /* filterContext */,transactionToken);
			});

		}

		_addFiltersForMembers(filterSpecs, visualization, transactionToken) {
			if (!filterSpecs.length) {
				return;
			}
			let localFilters = visualization.getLocalFilters();
			const dataItemList = visualization.getSlots().getDataItemList();
			filterSpecs.forEach(spec => {
				//if dataItem is added to slot or visualization, then add filters,
				// if dataItem is not added due to any reason, do not add filters
				const filterColumnId = spec.itemContext.itemId;
				let dataItem = dataItemList.find(dataItem => dataItem.getColumnId() === filterColumnId);
				if (dataItem) {
					if (spec.hasFilters) {
						localFilters.editFilter(spec.itemContext, spec.filterContext, transactionToken);
					} else {
						localFilters.addFilter(spec.itemContext, spec.filterContext, transactionToken);
					}
				}
			});
		}

		supports(source, target) {
			return target.type ==='widget.live' &&  source.type !=='widget';
		}

		accepts(source, target) {
			if (target && target.type === 'widget.live') {
				if (this.isInvalidVisDefinition(target)) {
					//No definitions so do not all metadata drag and drop
					return false;
				}

				if (this.content) {
					if (this.state && this.state.getError()) {
						return false;
					}
				}

				if ($(target.info.node).hasClass('liveWidgetPreview')) {
					return false;
				}

				if (this.content) {
					return this.visDnDUtils.accepts(source, target);
				}
			}
			return false;
		}

		onDrop(source,target) {
			if (target.type !== 'widget.live') {
				return;
			}

			let visDef = this.visualization.getDefinition();
			if (visDef.getState().getError()) {
				return;
			}
			if (!this.visExpandMode || (this.visExpandMode && !this.visExpandMode.onDrop(source))) {
				if (this.visRecommender) {
					const metadataPayload = this.visDnDUtils.onDrop(source);
					const droppedColumns = metadataPayload.columns;
					if (!this.visDnDUtils.validateSupportsOLAP(droppedColumns.map(c=>c.metadataColumn))) {
						this.dashboardAPI.getFeature('Notification').setMessage(StringResources.get('visualizationDoesNotSupportOLAP'));
						return;
					}
					let metadataColumns = _.pluck(droppedColumns, 'metadataColumn');

					const transactionToken = this.transaction.startTransaction();

					// We check for model filters first
					const modelFilters = metadataColumns && metadataColumns.filter((col) => col.isFilter());
					metadataColumns = metadataColumns && metadataColumns.filter((col) => !col.isFilter());

					let filterSpecsForMembers = [];
					if (metadataColumns.length) {
						filterSpecsForMembers = this.visDnDUtils.addMembersAsLocalFilters({
							visualizationAPI: this.visualization,
							columns: metadataPayload.columns,
							doUpdateFilters: false
						}, transactionToken);
						metadataColumns = _.pluck(droppedColumns, 'metadataColumn');
					}

					const endTransaction = () => {
						// Add the filters for items that are add as dataitems
						this._addFiltersForMembers(filterSpecsForMembers, this.visualization, transactionToken);
						this.transaction.endTransaction(transactionToken);
					};
					if (metadataColumns.length || modelFilters.length) {
						const sourceId = source.data.sourceId;
						const dataSource = this.visualization.getDataSource();
						// Create a transcation so that we can bundle the dataSource id change with the mapping

						if (!dataSource || dataSource.getId() != sourceId) {
							this.visualization.setDataSource(sourceId, transactionToken);
						}

						// The viz might not support the datasource so it might not be set
						let isDataSourceSet = !!this.visualization.getDataSource();
						if (isDataSourceSet) {
							this.addModelFilters(modelFilters, transactionToken);

							// convert to dataItems...no need to remove the filters
							const dataItems = metadataColumns.filter(mc => !mc.isFilter()).map((column) => {
								return {
									columnId: column.getId()
								};
							});
							if (dataItems.length > 0) {
								return this.visRecommender.addDataItems(dataItems, transactionToken).finally(() => {
									endTransaction();
								});
							}
						}
					}
					endTransaction();
				}
			}
		}
	};
});
